import { ArrowRight, Download, Mail } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CaseStudyMedia, CaseStudyReveal } from "@/components/CaseStudyMotion";
import { LanguageToggle } from "@/components/LanguageToggle";
import { SEO } from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { getProject, homeCards } from "@/data/projects";

const selectedIds = [
  "kasha-ai-personalization",
  "otis-ai-marketing",
  "alekbg-commerce-brand",
  "opentag-loyalty-system",
  "wellmaxi-brand-catalogue",
] as const;

const experience = [
  { company: "Kasha.io", role: "Head of Creative & UX/UI Design", roleBg: "Ръководител Creative & UX/UI Design", period: "Sep 2024 – Oct 2025", periodBg: "Сеп 2024 – Окт 2025" },
  { company: "Otis AI", role: "Head of UX/UI & Business Analyst", roleBg: "Ръководител UX/UI и бизнес анализатор", period: "Sep 2023 – Sep 2025", periodBg: "Сеп 2023 – Сеп 2025" },
  { company: "OpenTag", role: "Senior UX/UI Designer", roleBg: "Старши UX/UI дизайнер", period: "Jun 2022 – Sep 2023", periodBg: "Юни 2022 – Сеп 2023" },
];

const copy = {
  en: {
    label: "Portfolio Review",
    selected: "Selected Work",
    cv: "Download CV",
    contact: "Contact",
    eyebrow: "Senior / Lead Product Designer",
    title: "Khaled Kasmou",
    positioning: "UX/UI, Research & Product Strategy",
    intro: "End-to-end product design across SaaS, AI, B2B, B2C and digital commerce. I connect product thinking and research framing with UX architecture, interaction, interface craft, systems, prototyping, iteration and handoff.",
    prepared: "Prepared for",
    applying: "Applying for",
    generic: "A concise review of selected product design work",
    how: "How I work",
    work: "Selected case studies",
    workIntro: "Five public case studies showing product thinking, UX architecture and detailed interface work across distinct product contexts.",
    view: "View case study",
    contribution: "Contribution",
    breadth: "Breadth",
    breadthBody: "The portfolio spans AI and SaaS products, platform ecosystems, B2B and B2C commerce, loyalty and operator tools, and connected brand, catalogue and product systems.",
    experience: "Experience snapshot",
    close: "Let’s talk about the role",
    closeBody: "I’m open to conversations about senior and lead product design opportunities where thoughtful UX and strong interface craft matter.",
    email: "Email Khaled",
    linkedin: "LinkedIn",
  },
  bg: {
    label: "Преглед на портфолио",
    selected: "Подбрани проекти",
    cv: "Изтегли CV",
    contact: "Контакт",
    eyebrow: "Senior / Lead продуктов дизайнер",
    title: "Khaled Kasmou",
    positioning: "UX/UI, проучване и продуктова стратегия",
    intro: "Продуктов дизайн от край до край за SaaS, AI, B2B, B2C и дигитална търговия. Свързвам продуктовото мислене и рамката на проучването с UX архитектура, интеракции, детайлен интерфейс, системи, прототипиране, итерации и предаване.",
    prepared: "Подготвено за",
    applying: "Кандидатура за",
    generic: "Кратък преглед на подбрани продуктови проекти",
    how: "Как работя",
    work: "Подбрани казуси",
    workIntro: "Пет публични казуса с продуктово мислене, UX архитектура и детайлна интерфейсна работа в различни продуктови контексти.",
    view: "Виж казуса",
    contribution: "Принос",
    breadth: "Широта",
    breadthBody: "Портфолиото обхваща AI и SaaS продукти, платформени екосистеми, B2B и B2C търговия, loyalty и operator инструменти, както и свързани бранд, каталожни и продуктови системи.",
    experience: "Опит накратко",
    close: "Нека поговорим за ролята",
    closeBody: "Отворен съм за разговори за senior и lead продуктови позиции, в които обмисленият UX и силната интерфейсна изработка имат значение.",
    email: "Имейл до Khaled",
    linkedin: "LinkedIn",
  },
};

const capabilities = {
  en: ["Research", "Product Strategy", "UX Architecture", "Interaction Design", "UI Design", "Design Systems", "Prototyping", "Validation & Iteration", "Handoff"],
  bg: ["Проучване", "Продуктова стратегия", "UX архитектура", "Интеракционен дизайн", "UI дизайн", "Дизайн системи", "Прототипиране", "Валидация и итерации", "Предаване"],
};

const cleanParameter = (value: string | null) => {
  if (!value) return "";
  return Array.from(value)
    .map((character) => {
      const code = character.charCodeAt(0);
      return code < 32 || code === 127 ? " " : character;
    })
    .join("")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
};

const PortfolioReview = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const text = copy[language];
  const company = cleanParameter(searchParams.get("company"));
  const role = cleanParameter(searchParams.get("role"));
  const projects = selectedIds.flatMap((id) => {
    const project = getProject(id);
    const card = homeCards.find((item) => item.caseStudyId === id);
    return project && card ? [{ project, card }] : [];
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Portfolio Review | Khaled Kasmou — Senior Product Designer"
        description="Selected product design work by Khaled Kasmou across UX/UI, research, product strategy, systems and shipped digital products."
        path="/portfolio-review"
        noindex
      />

      <header className="sticky top-0 z-40 border-b border-border/80 bg-background/95 backdrop-blur-sm">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 sm:px-8 lg:px-12">
          <Link to="/portfolio-review" className="focus-premium min-w-0" aria-label="Portfolio Review home">
            <span className="block truncate text-sm font-semibold">Khaled Kasmou</span>
            <span className="hidden text-[11px] uppercase text-muted-foreground sm:block">{text.label}</span>
          </Link>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <LanguageToggle />
            <Button asChild variant="ghost" size="sm">
              <a href="/CV_Khaled_Kasmou.pdf" download aria-label={text.cv} title={text.cv}><Download aria-hidden="true" /><span className="hidden sm:inline">{text.cv}</span></a>
            </Button>
            <Button asChild size="sm">
              <a href="mailto:khaledkasmou@gmail.com" aria-label={text.contact}><Mail aria-hidden="true" /><span className="hidden sm:inline">{text.contact}</span></a>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-7xl px-5 pb-16 pt-16 sm:px-8 sm:pb-24 sm:pt-24 lg:px-12 lg:pb-32">
          <CaseStudyReveal>
            <div className="mb-8 flex flex-wrap items-center gap-2 text-xs font-mono uppercase text-muted-foreground">
              <span>{text.selected}</span><span aria-hidden="true">—</span><span>Sofia, Bulgaria</span>
            </div>
            {(company || role) ? (
              <p className="mb-6 max-w-3xl border-l-2 border-foreground pl-4 text-sm text-muted-foreground">
                {company && `${text.prepared} ${company}`}{company && role && " — "}{role && `${text.applying} ${role}`}
              </p>
            ) : <p className="mb-6 text-sm text-muted-foreground">{text.generic}</p>}
            <p className="mb-4 text-sm font-mono uppercase text-muted-foreground">{text.eyebrow}</p>
            <h1 className="max-w-5xl text-5xl font-normal leading-[0.95] sm:text-7xl lg:text-8xl">{text.title}</h1>
            <p className="mt-6 text-xl text-foreground sm:text-2xl">{text.positioning}</p>
            <p className="mt-8 max-w-3xl text-lg leading-relaxed text-muted-foreground sm:text-xl">{text.intro}</p>
          </CaseStudyReveal>
        </section>

        <section className="border-y border-border bg-card">
          <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:px-12">
            <CaseStudyReveal>
              <div className="mb-6 flex items-baseline gap-4"><span className="font-mono text-xs text-muted-foreground">01</span><h2 className="text-xl">{text.how}</h2></div>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-5">
                {capabilities[language].map((capability) => <li key={capability} className="border-t border-border pt-3 text-sm">{capability}</li>)}
              </ul>
            </CaseStudyReveal>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28 lg:px-12">
          <CaseStudyReveal className="mb-12 grid gap-6 lg:grid-cols-[1fr_1.2fr]">
            <div className="flex items-baseline gap-4"><span className="font-mono text-xs text-muted-foreground">02</span><h2 className="text-3xl sm:text-4xl">{text.work}</h2></div>
            <p className="max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">{text.workIntro}</p>
          </CaseStudyReveal>

          <div className="space-y-16 sm:space-y-24">
            {projects.map(({ project, card }, index) => {
              const isPrimary = index < 3;
              const title = language === "bg" ? project.title_bg : project.title;
              const challenge = language === "bg" ? project.challenge_bg : project.challenge;
              const roleText = language === "bg" ? project.role_bg : project.role;
              return (
                <CaseStudyReveal key={project.id}>
                  <article className={`grid items-center gap-7 ${isPrimary ? "lg:grid-cols-[1.35fr_0.65fr] lg:gap-14" : "lg:grid-cols-[0.9fr_1.1fr] lg:gap-12"}`}>
                    <Link to={`/project/${project.id}`} className={`focus-premium block ${!isPrimary && "lg:order-2"}`} aria-label={`${text.view}: ${project.company}`}>
                      <CaseStudyMedia className="review-project-media" frameClassName="bg-muted">
                        <img src={card.image} alt={`${project.company} product design case study`} loading={index > 0 ? "lazy" : "eager"} decoding="async" className={`w-full object-cover ${isPrimary ? "aspect-[16/10]" : "aspect-[16/9]"}`} />
                      </CaseStudyMedia>
                    </Link>
                    <div className={!isPrimary ? "lg:order-1" : ""}>
                      <div className="mb-5 flex items-center gap-3 font-mono text-xs uppercase text-muted-foreground"><span>{String(index + 1).padStart(2, "0")}</span><span className="h-px w-8 bg-border" /><span>{project.company}</span></div>
                      <h3 className={`${isPrimary ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"} leading-tight`}>{title}</h3>
                      <p className="mt-5 leading-relaxed text-muted-foreground">{challenge}</p>
                      <div className="mt-6 border-l border-border pl-4"><span className="block text-xs font-mono uppercase text-muted-foreground">{text.contribution}</span><span className="mt-1 block text-sm">{roleText}</span></div>
                      <ul className="mt-6 flex flex-wrap gap-2" aria-label="Project scope">
                        {project.tags.slice(0, isPrimary ? 5 : 4).map((tag) => <li key={tag} className="rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground">{tag}</li>)}
                      </ul>
                      <Button asChild variant="link" className="mt-6 h-auto p-0">
                        <Link to={`/project/${project.id}`}>{text.view}<ArrowRight aria-hidden="true" /></Link>
                      </Button>
                    </div>
                  </article>
                </CaseStudyReveal>
              );
            })}
          </div>
        </section>

        <section className="border-y border-border bg-foreground text-background">
          <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-[0.55fr_1.45fr] lg:px-12">
            <CaseStudyReveal><div className="flex items-baseline gap-4"><span className="font-mono text-xs text-background/70">03</span><h2 className="text-3xl">{text.breadth}</h2></div></CaseStudyReveal>
            <CaseStudyReveal delay={1}><p className="max-w-3xl text-xl leading-relaxed text-background/85 sm:text-2xl">{text.breadthBody}</p></CaseStudyReveal>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28 lg:px-12">
          <CaseStudyReveal>
            <div className="mb-10 flex items-baseline gap-4"><span className="font-mono text-xs text-muted-foreground">04</span><h2 className="text-3xl sm:text-4xl">{text.experience}</h2></div>
            <div className="divide-y divide-border border-y border-border">
              {experience.map((item) => <div key={item.company} className="grid gap-2 py-6 sm:grid-cols-[0.65fr_1.35fr_0.7fr] sm:items-baseline"><strong>{item.company}</strong><span className="text-muted-foreground">{language === "bg" ? item.roleBg : item.role}</span><span className="font-mono text-xs text-muted-foreground sm:text-right">{language === "bg" ? item.periodBg : item.period}</span></div>)}
            </div>
          </CaseStudyReveal>
        </section>

        <section className="border-t border-border">
          <CaseStudyReveal className="mx-auto max-w-7xl px-5 py-20 text-center sm:px-8 sm:py-28 lg:px-12">
            <p className="mb-4 font-mono text-xs uppercase text-muted-foreground">05 / Contact</p>
            <h2 className="text-4xl sm:text-6xl">{text.close}</h2>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">{text.closeBody}</p>
            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <Button asChild size="lg"><a href="mailto:khaledkasmou@gmail.com"><Mail aria-hidden="true" />{text.email}</a></Button>
              <Button asChild size="lg" variant="outline"><a href="/CV_Khaled_Kasmou.pdf" download><Download aria-hidden="true" />{text.cv}</a></Button>
              <Button asChild size="lg" variant="outline"><a href="https://www.linkedin.com/in/khaledkasmou/" target="_blank" rel="noopener noreferrer">{text.linkedin}<ArrowRight aria-hidden="true" /></a></Button>
            </div>
          </CaseStudyReveal>
        </section>
      </main>
    </div>
  );
};

export default PortfolioReview;